import React, { useEffect, useState } from 'react'
import { Modal, Spin } from 'antd'
import { useMsal } from '@azure/msal-react'
import { refreshAuthToken } from '../api-utils/token-refresh-interceptor'

interface SessionTimeoutWarningProps {
  warningTime?: number // Minutes before expiry to show warning (default: 5)
  sessionTimeout?: number // Total session timeout in minutes (default: 55)
}

/**
 * Component that automatically refreshes the session when it's about to expire
 */
export const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  warningTime = 5,
  sessionTimeout = 55,
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const { instance } = useMsal()

  useEffect(() => {
    // Calculate when to show warning (in milliseconds)
    const warningDelay = (sessionTimeout - warningTime) * 60 * 1000

    let warningTimer: NodeJS.Timeout

    const resetTimers = () => {
      // Clear existing timer
      if (warningTimer) clearTimeout(warningTimer)

      // Set warning timer to auto-refresh session
      warningTimer = setTimeout(async () => {
        setIsRefreshing(true)

        try {
          const success = await refreshAuthToken(instance)

          if (success) {
            // Session refreshed successfully
            setIsRefreshing(false)
          } else {
            // Failed to refresh, show error and redirect to home
            handleSessionExpired()
          }
        } catch (error) {
          console.error('Error refreshing session:', error)
          handleSessionExpired()
        }
      }, warningDelay)
    }

    const handleUserActivity = () => {
      // Reset timer on user activity
      if (!isRefreshing) {
        resetTimers()
      }
    }

    // Track user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart']
    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity)
    })

    // Initialize timer
    resetTimers()

    // Cleanup
    return () => {
      if (warningTimer) clearTimeout(warningTimer)
      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity)
      })
    }
  }, [warningTime, sessionTimeout, isRefreshing, instance])

  const handleSessionExpired = () => {
    setIsRefreshing(false)
    sessionStorage.clear()
    // Redirect to home page to re-authenticate
    window.location.href = '/'
  }

  return (
    <Modal
      title="Refreshing Session"
      open={isRefreshing}
      closable={false}
      footer={null}
    >
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '20px', fontSize: '16px' }}>
          Refreshing your session, please wait...
        </p>
      </div>
    </Modal>
  )
}
